import { useMemo } from "react";
import { useTable } from "react-table";

import ICarret from "assets/i-carret.js";
import IX from "assets/i-x.js";
import ModalLoader from "components/organism/ModalLoader";

const ReusableTable = ({
  columns,
  data = [],
  isLoading = false,
  emptyState = null,
  warningBanner = null,
  pagination = null,
  minWidth = "1149px",
  containerClassName = "",
  headerClassName = "",
  headerColClassName = "",
  bodyClassName = "",
  rowClassName = "",
  cellClassName = "",
  showLoader = true,
  renderHeader = null,
}) => {
  const tableColumns = useMemo(() => columns, [columns]);
  const tableData = useMemo(() => data, [data]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns: tableColumns,
      data: tableData,
    });

  const defaultHeaderRowClass =
    "flex items-center px-4 py-2 gap-[24px] bg-[#FaFaFa] border-b-[1px] border-solid border-[#D8D8D9]";
  const defaultHeaderColClass =
    "text-[13px] leading-[21px] tracking-[-0.4px] font-inter text-[#808285]";
  const defaultBodyRowClass =
    "flex items-center px-4 py-[10px] gap-[24px] border-b-[0.5px] border-solid border-[#D8D8D9]";
  const defaultBodyColClass =
    "text-[13px] leading-[21px] tracking-[-0.4px] font-inter truncate";

  return (
    <>
      <div
        className={`rounded-[4px] border-[0.5px] border-solid border-[#D8D8D9] ${containerClassName}`}
        style={{ minWidth }}
      >
        <div {...getTableProps()}>
          {renderHeader ? (
            renderHeader({ headerGroups, defaultHeaderRowClass, defaultHeaderColClass })
          ) : (
            headerGroups.map((headerGroup) => (
              <div
                {...headerGroup.getHeaderGroupProps()}
                className={headerClassName || defaultHeaderRowClass}
              >
                {headerGroup.headers.map((column) => (
                  <div
                    {...column.getHeaderProps()}
                    style={{
                      width: column.width || "auto",
                      minWidth: column.minWidth || "auto",
                      maxWidth: column.maxWidth || "none",
                      ...column.headerStyle,
                    }}
                  >
                    <span className={headerColClassName || defaultHeaderColClass}>
                      {column.render("Header")}
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {warningBanner}

        <div {...getTableBodyProps()}>
          {rows.length > 0 ? (
            rows.map((row) => {
              prepareRow(row);
              return (
                <div
                  {...row.getRowProps()}
                  className={rowClassName || defaultBodyRowClass}
                >
                  {row.cells.map((cell) => (
                    <div
                      {...cell.getCellProps()}
                      style={{
                        width: cell.column.width || "auto",
                        minWidth: cell.column.minWidth || "auto",
                        maxWidth: cell.column.maxWidth || "none",
                        ...cell.column.cellStyle,
                      }}
                    >
                      <span className={cellClassName || defaultBodyColClass}>
                        {cell.render("Cell")}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          ) : !isLoading ? (
            emptyState || (
              <div className="h-[494px] flex flex-col items-center justify-center gap-0.5 py-6">
                <p className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
                  No data available
                </p>
              </div>
            )
          ) : null}
        </div>
      </div>

      {pagination}

      {showLoader && <ModalLoader open={isLoading} />}
    </>
  );
};

export const TablePagination = ({
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  dataCount,
  totalCount,
  minWidth = "1149px",
  infoText,
}) => {
  return (
    <div
      className="flex items-center justify-between mt-4"
      style={{ minWidth }}
    >
      <p className="text-[11px]/[18px] italic font-inter text-[#808285]">
        {infoText || (
          <>
            *Menampilkan <span className="text-[#252526]">{dataCount}</span> dari{" "}
            {totalCount || dataCount} data.
          </>
        )}
      </p>

      <div className="flex items-center gap-2">
        <button
          onClick={onPrevPage}
          disabled={currentPage === 1}
          className="rotate-180 w-6 h-6 border border-solid border-[#D8D8D9] rounded-[4px] flex items-center justify-center disabled:opacity-50"
        >
          <ICarret />
        </button>
        <span className="text-[11px]/[18px] font-semibold font-inter text-[#252526] w-6 h-6 rounded-[4px] flex items-center justify-center shadow-[0_0_2px_0_rgba(0,0,0,0.32)]">
          {currentPage}
        </span>
        <button
          onClick={onNextPage}
          disabled={currentPage === totalPages}
          className="w-6 h-6 border border-solid border-[#D8D8D9] rounded-[4px] flex items-center justify-center disabled:opacity-50"
        >
          <ICarret />
        </button>
      </div>
    </div>
  );
};

export const TableWarningBanner = ({ message, onClose, show = true }) => {
  if (!show) return null;

  return (
    <div className="flex items-center justify-between gap-1 px-4 py-2 bg-[#F6E5B6]">
      <div className="flex items-center gap-1">
        <p className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#614E1A]">
          {message}
        </p>
      </div>
      {onClose && <IX onClick={onClose} color="#614E1A" />}
    </div>
  );
};

export const TableEmptyState = ({ imageSrc, title, subtitle }) => {
  return (
    <div className="h-[494px] flex flex-col items-center justify-center gap-0.5 py-6">
      {imageSrc && (
        <img
          className="w-[48px] h-[48px] object-contain"
          src={imageSrc}
          alt="empty-state"
        />
      )}
      <p className="text-[13px] leading-[21px] tracking-[-0.4px] font-inter-semibold text-[#252526]">
        {title}
      </p>
      {subtitle && (
        <p className="text-[11px] leading-[17.798px] tracking-[-0.4px] font-inter text-[#808285]">
          {subtitle}
        </p>
      )}
    </div>
  );
};

export default ReusableTable;
